/* eslint-disable @typescript-eslint/no-var-requires */
import getLang from 'I18n/utils/getLang';
import setDocumentTitle from 'I18n/utils/setDocumentTitle';
import { AppRoot, renderAppRoot } from 'quartz-core/internal/root-renderer';
import { initializeI18n } from 'quartz-i18n/initialize';
import { initializeTracking } from 'quartz-tracking/initialize';
import { getQuartzConfig } from 'quartz-config/evaluable';
import { invariant } from 'quartz/invariant';
import { initializeHistory } from 'quartz-routing/initialize';
import { mark } from './perf';
import { isPromiseLike } from './isPromiseLike';
import quartzCoreLang from 'i2l?sync&query=sporks!../../lang/en.lyaml';
import { Metrics } from './metrics';
import { getBasenameFromRouting } from '../util/getBasenameFromRouting';
import { renderApp as srcdocRenderApp } from './srcdoc-app-renderer';
import { jsx as _jsx } from "react/jsx-runtime";
export async function renderApp({
  prepareApp,
  AppComponent,
  getLocaleInfo
}, {
  rhumbConfig,
  lang,
  container,
  LayoutComponent,
  adapter
}) {
  const quartzConfig = getQuartzConfig();
  /* invariant(quartzConfig.type === 'application') */
  ;
  const isSrcdocMode = quartzConfig.type === 'application' && quartzConfig.auth === 'none' && quartzConfig.UNSAFE_EXPERIMENTAL_srcdocMode;
  if (isSrcdocMode) {
    return srcdocRenderApp({
      prepareApp,
      AppComponent,
      getLocaleInfo
    }, {
      rhumbConfig,
      lang,
      container
    });
  }
  const {
    htmlTitleLangKey,
    historyBasename: historyBasenameFromConfig,
    UNSAFE_EXPERIMENTAL_routing: routing,
    rhumbTrackVisibility
  } = quartzConfig;
  const appBasename = historyBasenameFromConfig || getBasenameFromRouting(routing);
  const {
    userId,
    localeInfoFallback,
    gates
  } = await adapter.initialize(new URLSearchParams(window.location.search));

  // These two checks must be separate variables because the eval-loader
  // cannot statically evaluate conditions that mix quartzConfig with
  // runtime values. The quartzConfig check is resolved at build time,
  // the isGraniteEnabled check is resolved at runtime.
  const isGraniteHost = quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions && quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions.type === 'host';
  let isGraniteEnabled = false;
  let graniteModule;
  if ( /* quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions && quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions.type === 'remote' && quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions.useRuntimeHosting */false) {
    graniteModule = require('../granite/GraniteLocalApp');
    isGraniteEnabled = graniteModule.isGraniteEnabledForCurrentApp(gates);
  }
  const historyBasename = isGraniteHost ? '/' : appBasename;
  if (!historyBasename) {
    throw new Error('historyBasename is required');
  }
  const history = initializeHistory(historyBasename);
  const {
    isPublicI18n,
    registerFn
  } = adapter.getI18nConfig();
  const localeInfoResult = getLocaleInfo === null || getLocaleInfo === void 0 ? void 0 : getLocaleInfo();
  let localeInfo = isPromiseLike(localeInfoResult) ? await localeInfoResult : localeInfoResult;
  if (!isPublicI18n && !localeInfo) {
    localeInfo = localeInfoFallback;
  }
  const [result] = await Promise.all([isGraniteEnabled ? undefined : prepareApp(), initializeI18n({
    langs: [lang, quartzCoreLang],
    createPublicProvider: isPublicI18n,
    localeInfo,
    registerFn,
    trackSetLocale: () => {
      Metrics.counter('i18n_setLocale_called').increment();
    }
  })]);
  const {
    email,
    hstc,
    hubId
  } = adapter.getTrackingProperties();
  initializeTracking({
    lang: getLang(),
    email: email !== null && email !== void 0 ? email : null,
    hstc: hstc !== null && hstc !== void 0 ? hstc : null,
    hubId
  });
  mark('quartz.prepareAppFinished');
  if (htmlTitleLangKey) {
    setDocumentTitle(htmlTitleLangKey);
  }
  let maybeRoot;
  if (result && 'redirect' in result) {
    window.location.href = result.redirect;
  } else {
    var _adapter$getCopilotOp, _result$onAfterRender;
    const copilotOptions = (_adapter$getCopilotOp = adapter.getCopilotOptions) === null || _adapter$getCopilotOp === void 0 ? void 0 : _adapter$getCopilotOp.call(adapter);
    let appContent;
    if (isGraniteEnabled && graniteModule) {
      const GraniteLocalApp = graniteModule.GraniteLocalApp;
      appContent = /*#__PURE__*/_jsx(GraniteLocalApp, {
        gates: gates,
        appHistory: history,
        prepareApp: prepareApp,
        AppComponent: AppComponent,
        LayoutComponent: LayoutComponent,
        rhumbConfig: rhumbConfig,
        rhumbTrackVisibility: rhumbTrackVisibility,
        userId: userId,
        adapter: adapter
      });
    } else if (LayoutComponent) {
      appContent = /*#__PURE__*/_jsx(LayoutComponent, {
        children: /*#__PURE__*/_jsx(AppComponent, Object.assign({}, result === null || result === void 0 ? void 0 : result.props))
      });
    } else {
      appContent = /*#__PURE__*/_jsx(AppComponent, Object.assign({}, result === null || result === void 0 ? void 0 : result.props));
    }
    if (isGraniteEnabled) {
      maybeRoot = await renderAppRoot(appContent, Object.assign({
        legacyRoot: adapter.shouldUseLegacyRoot(),
        strictMode: quartzConfig.reactStrictMode
      }, copilotOptions, {
        container
      }));
    } else {
      maybeRoot = await renderAppRoot( /*#__PURE__*/_jsx(AppRoot, {
        history: history,
        rhumbConfig: rhumbConfig,
        rhumbTrackVisibility: rhumbTrackVisibility,
        userId: userId,
        skipRhumbProvider: !!isGraniteHost,
        children: appContent
      }), Object.assign({
        legacyRoot: adapter.shouldUseLegacyRoot(),
        strictMode: quartzConfig.reactStrictMode
      }, copilotOptions, {
        container
      }));
    }
    result === null || result === void 0 || (_result$onAfterRender = result.onAfterRender) === null || _result$onAfterRender === void 0 || _result$onAfterRender.call(result);
    if ( /* 'plugins' in quartzConfig */false) {
      const {
        getAppExtensions
      } = require('./plugins/app-plugins');
      for (const plugin of getAppExtensions()) {
        plugin.hooks.onAfterRender();
      }
    }
    return maybeRoot;
  }
}